import styles from './BundleCTA.scss';

import cx from 'classnames';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentType, ExternalLink, InternalLink } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Button } from 'src/components/Button/Button';
import { ButtonLinkV2, ButtonLinkV2Props } from 'src/components/Button/ButtonLinkV2';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { Environment } from 'src/utils/Environment';
import { CartContext } from '../Cart/CartContext';

export const CONNECT_TO_SALES_PATH = 'contact/connect-to-sales';

interface BundleCTAProps extends Omit<ButtonLinkV2Props, 'variant' | 'isFullWidth'> {
  checkoutLink?: FlatEntry<ExternalLink>;
  dcsId?: string;
  isSelfServicePurchaseEnabled?: boolean;
  shouldUseCheckoutLink?: boolean;
  solutionTitle?: string;
}

export const BundleCTA: React.FC<BundleCTAProps> = ({
  className,
  checkoutLink,
  isSelfServicePurchaseEnabled = false,
  dcsId,
  shouldUseCheckoutLink = false,
  solutionTitle,
  ...props
}) => {
  const classNames = cx(className, styles.button);
  const { cart, addBundleById } = useContext(CartContext);
  const { t } = useTranslation();
  const isBundleAlreadyInCart = dcsId && cart.bundle?.id === dcsId;

  const checkoutCta =
    (
      <ButtonLinkV2
        isFullWidth
        entry={checkoutLink}
        className={classNames}
        {...props}
      />
    );

  const contactSalesCta = (
    <ButtonLinkV2
      entry={{
        ...checkoutLink,
        contentType: ContentType.INTERNAL_LINK,
        page: { slug: CONNECT_TO_SALES_PATH },
        title: t('contact-sales'),
      } as FlatEntry<InternalLink>}
    />
  );

  if (Environment.current.isExistingMerchant || !isSelfServicePurchaseEnabled) {
    return contactSalesCta;
  }

  if (isBundleAlreadyInCart) {
    return (
      <Button
        disabled
        variant={checkoutLink?.variant}
        isFullWidth
      >
        {t('pricing-detail-table.checkout-cta-already-in-cart')}
      </Button>
    );
  }

  if (!dcsId || shouldUseCheckoutLink) {
    return checkoutCta;
  }

  return (
    <Button
      onClick={() => addBundleById(dcsId, { needsConfirm: true, needsRedirect: true, solutionTitle })}
      className={classNames}
      variant={checkoutLink?.variant}
      isFullWidth
    >
      {t('pricing-detail-table.add-to-cart')}
    </Button>
  );
};
